import { useCallback, useEffect } from 'react'
import { CommandChannelMsg } from '@zoom/videosdk'
import { useUI } from '../../../context/UIContext'
import { useZoomContext } from '../../../context/ZoomContext'
import { useTranslation } from 'react-i18next'
import useRaisedHands from '../../../hooks/useRaisedHands'
import { useContentSharing } from '../../../context/ContentSharingContext'
import { useMentorAIQuery } from '../../../hooks/useMentorAIQuery'
import { useMentorAI } from '../../../context/MentorAIContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NotificationWithCross } from '../../../components/NotificationWithCross'

export type CommandChannelAction = {
    type: string
    action: string
    message?: any
    chatId?: string
    escalated?: boolean
}

const useCommandChannel = () => {
    const { t } = useTranslation('notifications')
    const { showToast, openImageModal, setIsHelpActive } = useUI()
    const { zoomClient, currentUserInfo, raisedHands, setSidebarMode, sidebarMode, setUnReadMessages } =
        useZoomContext()
    const { toggleRaiseHand } = useRaisedHands()
    const { setCurrentSharedStep, setIsContentShared } = useContentSharing()
    const { setEscalated } = useMentorAI()
    const { invalidateChatHistory } = useMentorAIQuery()

    const handleTextMsg = useCallback(
        (payload: CommandChannelMsg) => {
            try {
                const message = payload.text
                if (!message) {
                    console.error('No message content found in payload:', payload)
                    return
                }
                const data = JSON.parse(message)

                switch (data.type) {
                    case 'content_sharing':
                        switch (data.action) {
                            case 'start':
                                setCurrentSharedStep(data.stepId)
                                setIsContentShared(true)
                                setSidebarMode('content')
                                break
                            case 'stop':
                                setIsContentShared(false)
                                setSidebarMode(null)
                                showToast(t('commandChannel.contentSharingEnded'), {
                                    id: 'content-sharing-ended',
                                    duration: 5000,
                                })
                                break
                            case 'sidebar_change_mentor':
                                setSidebarMode('mentor')
                                break
                            case 'sidebar_change_content':
                                setSidebarMode('content')
                                break
                            case 'sidebar_close':
                                setSidebarMode(null)
                                break
                            case 'sidebar_mentor_open':
                                setSidebarMode('mentor')
                                break
                            case 'sidebar_content_open':
                                setSidebarMode('content')
                                break
                            case 'navigate':
                                setCurrentSharedStep(data.stepId)
                                if (sidebarMode !== 'content') {
                                    setSidebarMode('content')
                                }
                                break
                            case 'share_image':
                                if (payload.senderId !== currentUserInfo?.userId) {
                                    openImageModal({
                                        src: data.src,
                                        alt: data.alt || 'Shared image',
                                    })
                                }
                                break
                            default:
                                return console.warn('Unknown command channel message', data)
                        }
                        break
                    case 'mentor_ai':
                        switch (data.action) {
                            case 'new_message':
                                setUnReadMessages(prev => {
                                    const newState = { ...prev, mentor: true }
                                    return newState
                                })
                                invalidateChatHistory(data.chatId)
                                if (data.escalated) {
                                    setEscalated(prev => (prev === false ? true : prev))
                                    setSidebarMode('mentor')
                                    showToast(
                                        <NotificationWithCross id="mentor-escalated">
                                            <FontAwesomeIcon
                                                icon={['far', 'user-headset']}
                                                style={{ color: 'white', marginRight: '8px' }}
                                            />
                                            {t(
                                                'mentor.escalated',
                                                'Your question has been escalated to a human expert'
                                            )}
                                        </NotificationWithCross>,
                                        {
                                            id: 'mentor-escalated',
                                            duration: Infinity,
                                        }
                                    )
                                }
                                break
                            case 'sharer_typing':
                                if (payload.senderId !== currentUserInfo?.userId) {
                                    setUnReadMessages(prev => ({
                                        ...prev,
                                        sharerTyping: true,
                                    }))
                                }
                                break
                            case 'sharer_asking':
                                if (payload.senderId !== currentUserInfo?.userId) {
                                    setUnReadMessages(prev => ({
                                        ...prev,
                                        sharerTyping: false,
                                        sharerAsking: true,
                                    }))
                                }
                                break
                            case 'sharer_done':
                                if (payload.senderId !== currentUserInfo?.userId) {
                                    setUnReadMessages(prev => ({
                                        ...prev,
                                        sharerTyping: false,
                                        sharerAsking: false,
                                    }))
                                }
                                break

                            case 'hr_ended':
                                showToast(t('mentor.hr_ended', 'The mentor has completed help'))
                                setIsHelpActive(false)

                                break
                        }
                        break
                    case 'disableMic':
                        if (!zoomClient.isManager())
                            showToast(t('commandChannel.hostMutedParticipants'), { id: 'disableMic' })
                        break
                    case 'enableMic':
                        if (!zoomClient.isManager())
                            showToast(t('commandChannel.hostEnabledMicrophones'), { id: 'enableMic' })
                        break
                    case 'toggle-raise-hand':
                        const { userId, isHandRaised, options } = data
                        if (!isHandRaised && !raisedHands.some(item => item.userId === userId)) return // Avoid duplicates
                        if (userId !== currentUserInfo?.userId || options.force)
                            toggleRaiseHand(userId, { notify: false, ...options })
                        break
                    case 'help-request':
                        // Don't show toast to the user who requested help
                        if (payload.senderId !== currentUserInfo?.userId) {
                            const teamText = data.teamNumber ? ` (Team ${data.teamNumber})` : ''
                            showToast(t('commandChannel.helpRequested', { team: teamText }), {
                                id: `help-request-${data.timestamp}`,
                                duration: 5000, // Show for 5 seconds
                            })
                        }
                        break
                    default:
                        return console.warn('Unknown command channel message', data)
                }
            } catch (error) {
                console.error('Error parsing command message in useCommandChannel:', error, 'Payload:', payload)
            }
        },
        [
            zoomClient,
            showToast,
            t,
            raisedHands,
            currentUserInfo?.userId,
            toggleRaiseHand,
            setCurrentSharedStep,
            setIsContentShared,
            setSidebarMode,
            sidebarMode,
            openImageModal,
            setUnReadMessages,
            invalidateChatHistory,
            setIsHelpActive,
            setEscalated,
        ]
    )

    useEffect(() => {
        zoomClient.on('command-channel-message', handleTextMsg)
        return () => {
            zoomClient.off('command-channel-message', handleTextMsg)
        }
    }, [zoomClient, handleTextMsg])

    return {
        handleTextMsg,
    }
}

export default useCommandChannel
